import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import dayjs from "dayjs";

import { bangTinApi } from "src/1/apis/bang_tin.api";

/**
 * Định dạng rút gọn số tiền hiển thị trên trục Y và huy hiệu thống kê
 * Ví dụ: 120.000.000 -> 120tr; 50.000.000 -> 50tr; 1.500.000.000 -> 1,5 tỷ
 */
const formatShortMoney = (val) => {
  if (!val || val === 0) return "0";
  const num = Number(val);
  if (num >= 1_000_000_000) {
    const b = num / 1_000_000_000;
    const formatted = b % 1 === 0 ? b : b.toFixed(1);
    return `${formatted.toString().replace(".", ",")} tỷ`;
  }
  if (num >= 1_000_000) {
    const m = num / 1_000_000;
    const formatted = m % 1 === 0 ? m : m.toFixed(1);
    return `${formatted.toString().replace(".", ",")}tr`;
  }
  if (num >= 1_000) {
    const k = num / 1_000;
    const formatted = k % 1 === 0 ? k : k.toFixed(0);
    return `${formatted.toString().replace(".", ",")}k`;
  }
  return `${num}`;
};

/**
 * Định dạng số tiền đầy đủ kèm đơn vị VNĐ
 */
const formatFullMoney = (val) => {
  return (Number(val) || 0).toLocaleString("vi-VN") + " đ";
};

/**
 * Phân tích dữ liệu thực tế từ API thành danh sách điểm biểu đồ 7 ngày
 * Tuyệt đối không dùng dữ liệu giả lập (mockup/fallback)
 */
const parseChartData = (apiResponse) => {
  // Tạo khung 7 ngày tính từ hôm nay lùi về trước làm mốc cơ bản
  const base7Days = [];
  for (let i = 6; i >= 0; i--) {
    const d = dayjs().subtract(i, "day");
    base7Days.push({
      dateStr: d.format("YYYY-MM-DD"),
      shortDate: d.format("DD/MM"),
      date: d.format("DD/MM"),
      fullDate: d.format("DD/MM/YYYY"),
      isToday: i === 0,
      amount: 0,
    });
  }

  if (!apiResponse) return base7Days;

  // Trích xuất mảng dữ liệu từ các cấu trúc phản hồi thông dụng của BE
  const rawList = Array.isArray(apiResponse)
    ? apiResponse
    : Array.isArray(apiResponse?.data)
      ? apiResponse.data
      : Array.isArray(apiResponse?.items)
        ? apiResponse.items
        : Array.isArray(apiResponse?.list)
          ? apiResponse.list
          : Array.isArray(apiResponse?.rows)
            ? apiResponse.rows
            : null;

  if (rawList && rawList.length > 0) {
    // Nếu BE trả về trực tiếp mảng số
    if (typeof rawList[0] === "number") {
      const offset = Math.max(0, rawList.length - 7);
      return base7Days.map((day, idx) => ({
        ...day,
        amount: Number(rawList[offset + idx]) || 0,
      }));
    }

    // Nếu BE trả về danh sách đối tượng có ngày và số tiền
    const lookup = {};
    rawList.forEach((item) => {
      if (!item) return;

      const rawDate =
        item.date ||
        item.ngay ||
        item.created_at ||
        item.createdAt ||
        item.time ||
        item.day ||
        item.date_format ||
        item.key;

      const rawAmount =
        item.total ??
        item.tong_tien ??
        item.total_amount ??
        item.total_price ??
        item.amount ??
        item.value ??
        item.tien_hang ??
        item.sum ??
        item.val ??
        0;

      const numVal = Number(rawAmount) || 0;

      if (rawDate) {
        const parsed = dayjs(rawDate);
        if (parsed.isValid()) {
          lookup[parsed.format("YYYY-MM-DD")] = numVal;
          lookup[parsed.format("DD/MM")] = numVal;
          lookup[parsed.format("DD/MM/YYYY")] = numVal;
        }
        lookup[String(rawDate).trim()] = numVal;
      }
    });

    return base77DaysMapping(base7Days, lookup);
  }

  // Nếu BE trả về dạng đối tượng key-value theo ngày { "YYYY-MM-DD": amount }
  if (typeof apiResponse === "object" && !Array.isArray(apiResponse)) {
    return base7Days.map((day) => {
      const matched =
        apiResponse[day.dateStr] ??
        apiResponse[day.shortDate] ??
        apiResponse[day.fullDate] ??
        0;
      return {
        ...day,
        amount: Number(matched) || 0,
      };
    });
  }

  return base7Days;
};

const base77DaysMapping = (baseDays, lookup) => {
  return baseDays.map((day) => {
    const matched =
      lookup[day.dateStr] ?? lookup[day.shortDate] ?? lookup[day.fullDate] ?? 0;
    return {
      ...day,
      amount: matched,
    };
  });
};

/**
 * Component Area Chart Card dùng chung
 */
function AreaChartCard({ title, theme, data, isLoading }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Tính toán thang đo trục Y và các chỉ số thống kê thực tế
  const { total, avg, maxValItem, niceMax, ticks } = useMemo(() => {
    const totalAmount = data.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0,
    );
    const avgAmount =
      data.length > 0 ? Math.round(totalAmount / data.length) : 0;
    const maxItem = [...data].sort(
      (a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0),
    )[0] || { amount: 0, date: "" };

    const rawMax = Math.max(...data.map((d) => Number(d.amount) || 0), 0);

    // Nếu toàn bộ dữ liệu thực tế bằng 0
    if (rawMax === 0) {
      return {
        total: 0,
        avg: 0,
        maxValItem: { amount: 0, date: "-" },
        niceMax: 0,
        ticks: [0],
      };
    }

    // Tính bước nhảy trục Y đẹp (4 khoảng tương ứng 5 đường kẻ ngang)
    const targetStep = rawMax / 4;
    const power = Math.pow(10, Math.floor(Math.log10(targetStep)));
    const fraction = targetStep / power;

    let niceFraction = 1;
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 2.5) niceFraction = 2.5;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;

    const step = niceFraction * power;
    const computedNiceMax = step * 4;
    const tickList = [0, step, step * 2, step * 3, step * 4];

    return {
      total: totalAmount,
      avg: avgAmount,
      maxValItem: maxItem,
      niceMax: computedNiceMax,
      ticks: tickList,
    };
  }, [data]);

  // Kích thước SVG tinh chỉnh gọn gàng, tiết kiệm không gian
  const svgWidth = 520;
  const svgHeight = 160;
  const paddingLeft = 48;
  const paddingRight = 14;
  const paddingTop = 14;
  const paddingBottom = 22;

  const chartAreaWidth = svgWidth - paddingLeft - paddingRight;
  const chartAreaHeight = svgHeight - paddingTop - paddingBottom;
  const baselineY = svgHeight - paddingBottom;

  const getX = (index) => {
    if (data.length <= 1) return paddingLeft + chartAreaWidth / 2;
    return paddingLeft + (index * chartAreaWidth) / (data.length - 1);
  };

  const getY = (val) => {
    if (niceMax <= 0) return baselineY;
    const clamped = Math.max(0, Math.min(val, niceMax));
    return baselineY - (clamped / niceMax) * chartAreaHeight;
  };

  // Tạo tọa độ các điểm
  const points = useMemo(() => {
    return data.map((pt, i) => ({
      x: getX(i),
      y: getY(pt.amount),
      data: pt,
    }));
  }, [data, niceMax]);

  // Sinh đường cong Spline mượt mà (Cubic Bezier curve)
  const splinePath = useMemo(() => {
    if (points.length === 0) return "";
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

    let path = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i === 0 ? 0 : i - 1];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;

      const tension = 0.2;
      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;

      path += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(
        1,
      )} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return path;
  }, [points]);

  // Vùng diện tích Area bên dưới đường kẻ
  const areaPath = useMemo(() => {
    if (points.length === 0) return "";
    return `${splinePath} L ${points[points.length - 1].x.toFixed(
      1,
    )} ${baselineY} L ${points[0].x.toFixed(1)} ${baselineY} Z`;
  }, [splinePath, points, baselineY]);

  // Thông tin điểm đang hover
  const activePoint = hoveredIndex !== null ? points[hoveredIndex] : null;

  // Tính toán vị trí Tooltip đảm bảo không tràn lề
  const tooltipWidth = 108;
  const tooltipHeight = 38;
  let tooltipX = 0;
  let tooltipY = 0;

  if (activePoint) {
    tooltipX = activePoint.x - tooltipWidth / 2;
    if (tooltipX < paddingLeft) tooltipX = paddingLeft;
    if (tooltipX + tooltipWidth > svgWidth - paddingRight) {
      tooltipX = svgWidth - paddingRight - tooltipWidth;
    }
    tooltipY = Math.max(4, activePoint.y - tooltipHeight - 6);
  }

  const dateRangeText =
    data.length >= 2
      ? `${data[0].date} - ${data[data.length - 1].date}`
      : "7 ngày qua";

  return (
    <div className="bg-white rounded-xl p-3.5 sm:p-4 shadow-2xs border border-slate-200/90 flex flex-col justify-between transition-all duration-200 hover:shadow-sm">
      <div>
        {/* Header card */}
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-center gap-2.5">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center border shadow-2xs shrink-0 ${theme.iconBoxClass}`}
            >
              {theme.icon}
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-bold text-slate-800 leading-tight">
                {title}
              </h2>
              <p className="text-sm font-medium text-slate-400 mt-0.5 flex items-center gap-1.5">
                {isLoading ? (
                  <span className="text-blue-500 animate-pulse">
                    Đang đồng bộ dữ liệu...
                  </span>
                ) : (
                  <span>7 ngày gần nhất • {dateRangeText}</span>
                )}
              </p>
            </div>
          </div>

          {/* Badge tổng tiền */}
          <div
            className={`px-2.5 py-1 rounded-lg border text-right shrink-0 ${theme.badgeClass}`}
          >
            <span className="block text-xs font-semibold uppercase tracking-wider opacity-75">
              Tổng 7 ngày
            </span>
            <span className="text-xs sm:text-sm font-extrabold tracking-tight">
              {isLoading ? "..." : formatShortMoney(total)}
            </span>
          </div>
        </div>

        {/* SVG Area Chart */}
        <div className="mt-2.5 relative w-full overflow-x-auto select-none">
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="w-full h-auto min-w-[320px]"
          >
            <defs>
              <linearGradient id={theme.gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={theme.primaryColor}
                  stopOpacity="0.3"
                />
                <stop
                  offset="85%"
                  stopColor={theme.primaryColor}
                  stopOpacity="0.04"
                />
                <stop
                  offset="100%"
                  stopColor={theme.primaryColor}
                  stopOpacity="0.0"
                />
              </linearGradient>
            </defs>

            {/* Trục tung (Y-axis): Đường kẻ gióng ngang & Nhãn viết tắt số tiền */}
            {ticks.map((tickVal) => {
              const y = getY(tickVal);
              return (
                <g key={tickVal}>
                  <line
                    x1={paddingLeft}
                    y1={y}
                    x2={svgWidth - paddingRight}
                    y2={y}
                    stroke="#f1f5f9"
                    strokeWidth="1"
                    strokeDasharray={tickVal === 0 ? "none" : "3 3"}
                  />
                  <text
                    x={paddingLeft - 6}
                    y={y + 3}
                    fill="#94a3b8"
                    fontSize="9.5"
                    fontWeight="500"
                    textAnchor="end"
                  >
                    {formatShortMoney(tickVal)}
                  </text>
                </g>
              );
            })}

            {/* Vùng Area Gradient */}
            <path d={areaPath} fill={`url(#${theme.gradientId})`} />

            {/* Đường nét chính (Spline Stroke) */}
            <path
              d={splinePath}
              fill="none"
              stroke={theme.primaryColor}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {/* Trục hoành (X-axis): Nhãn 7 ngày */}
            {data.map((pt, i) => {
              const isSelected = hoveredIndex === i;
              return (
                <g key={i}>
                  <text
                    x={getX(i)}
                    y={svgHeight - 6}
                    fill={
                      isSelected
                        ? theme.primaryColor
                        : pt.isToday
                          ? "#0f172a"
                          : "#64748b"
                    }
                    fontSize="10"
                    fontWeight={pt.isToday || isSelected ? "700" : "500"}
                    textAnchor="middle"
                  >
                    {pt.date}
                  </text>
                  {pt.isToday && (
                    <circle
                      cx={getX(i)}
                      cy={svgHeight - 1}
                      r="1.5"
                      fill={theme.primaryColor}
                    />
                  )}
                </g>
              );
            })}

            {/* Tương tác khi Hover vào từng điểm dữ liệu */}
            {points.map((pt, i) => {
              const isHovered = hoveredIndex === i;
              return (
                <g
                  key={i}
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {/* Vùng bắt chuột rộng rãi */}
                  <circle cx={pt.x} cy={pt.y} r="15" fill="transparent" />

                  {/* Vòng hào quang khi hover */}
                  {isHovered && (
                    <circle
                      cx={pt.x}
                      cy={pt.y}
                      r="7.5"
                      fill={theme.primaryColor}
                      opacity="0.22"
                    />
                  )}

                  {/* Nốt điểm dữ liệu */}
                  <circle
                    cx={pt.x}
                    cy={pt.y}
                    r={isHovered ? "4.5" : "3"}
                    fill="#ffffff"
                    stroke={theme.primaryColor}
                    strokeWidth="2"
                    className="transition-all duration-150"
                  />
                </g>
              );
            })}

            {/* Tooltip & Đường gióng dọc hiển thị khi Hover */}
            {activePoint && (
              <g className="pointer-events-none">
                {/* Đường gióng dọc đứt nét */}
                <line
                  x1={activePoint.x}
                  y1={paddingTop}
                  x2={activePoint.x}
                  y2={baselineY}
                  stroke={theme.primaryColor}
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  opacity="0.5"
                />

                {/* Hộp Tooltip */}
                <rect
                  x={tooltipX}
                  y={tooltipY}
                  width={tooltipWidth}
                  height={tooltipHeight}
                  rx="5"
                  fill="#0f172a"
                  opacity="0.94"
                />

                {/* Ngày tháng */}
                <text
                  x={tooltipX + tooltipWidth / 2}
                  y={tooltipY + 14}
                  fill="#94a3b8"
                  fontSize="9.5"
                  fontWeight="500"
                  textAnchor="middle"
                >
                  {activePoint.data.fullDate}
                  {activePoint.data.isToday ? " (Hôm nay)" : ""}
                </text>

                {/* Giá trị tiền đầy đủ */}
                <text
                  x={tooltipX + tooltipWidth / 2}
                  y={tooltipY + 28}
                  fill="#ffffff"
                  fontSize="10.5"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {formatFullMoney(activePoint.data.amount)}
                </text>
              </g>
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}

/**
 * Component hiển thị 2 Area Chart:
 * Tự gọi 2 API:
 * - bangTinApi.getImportTotalRecentDays() -> Tổng tiền hàng nhập 7 ngày qua
 * - bangTinApi.getExportTotalRecentDays() -> Tổng tiền hàng xuất 7 ngày qua
 * Hoàn toàn hiển thị theo dữ liệu API thực tế, không dùng mockup.
 */
export default function NewsCharts() {
  // 1. Gọi API lấy dữ liệu tiền hàng nhập 7 ngày
  const { data: importApiRes, isLoading: isLoadingImport } = useQuery(
    ["news-feed-recent-import-total"],
    () => bangTinApi.getImportTotalRecentDays(),
    {
      select: (res) => res?.data?.data ?? res?.data,
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
    },
  );

  // 2. Gọi API lấy dữ liệu tiền hàng xuất 7 ngày
  const { data: exportApiRes, isLoading: isLoadingExport } = useQuery(
    ["news-feed-recent-export-total"],
    () => bangTinApi.getExportTotalRecentDays(),
    {
      select: (res) => res?.data?.data ?? res?.data,
      refetchInterval: 60000,
      refetchOnWindowFocus: false,
    },
  );

  // Ánh xạ dữ liệu hàng nhập thực tế từ API
  const chartImportData = useMemo(() => {
    return parseChartData(importApiRes);
  }, [importApiRes]);

  // Ánh xạ dữ liệu hàng xuất thực tế từ API
  const chartExportData = useMemo(() => {
    return parseChartData(exportApiRes);
  }, [exportApiRes]);

  // Theme màu sắc đặc trưng cho Hàng Nhập (Xanh dương - Blue)
  const importTheme = {
    primaryColor: "#2563eb",
    gradientId: "importAreaGradient",
    iconBoxClass: "!bg-blue-50 !text-blue-600 !border-blue-200",
    badgeClass: "!bg-blue-50 !text-blue-700 !border-blue-200",
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.2"
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
        />
      </svg>
    ),
  };

  // Theme màu sắc đặc trưng cho Hàng Xuất (Cam ấm - Orange)
  const exportTheme = {
    primaryColor: "#ea580c",
    gradientId: "exportAreaGradient",
    iconBoxClass: "!bg-orange-50 !text-orange-600 !border-orange-200",
    badgeClass: "!bg-orange-50 !text-orange-700 !border-orange-200",
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.2"
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    ),
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 sm:gap-4">
      {/* Chart bên trái: Tổng tiền hàng nhập 7 ngày qua */}
      <AreaChartCard
        title="Tổng tiền hàng nhập 7 ngày qua"
        theme={importTheme}
        data={chartImportData}
        isLoading={isLoadingImport}
      />

      {/* Chart bên phải: Tổng tiền hàng xuất 7 ngày qua */}
      <AreaChartCard
        title="Tổng tiền hàng xuất 7 ngày qua"
        theme={exportTheme}
        data={chartExportData}
        isLoading={isLoadingExport}
      />
    </div>
  );
}
